import type { WavespeedModelConfig } from '../types';
import { clampTo } from '../../tools';

export const openaiConfigs: Record<string, WavespeedModelConfig> = {
    'gpt-image-2.5-flare-text-to-image': {
        // https://wavespeed.ai/zh-CN/models/openai/gpt-image-2.5-flare/text-to-image
        apiPath: '/openai/gpt-image-2.5-flare/text-to-image',
        pollApiPath: '/predictions/${requestId}/result',
        enableTranslation: false,
        buildInput: (params) => {
            const { prompt, aspectRatio, image_resolution, providerOptions } = params;
            return {
                prompt: prompt,
                aspect_ratio: clampTo(aspectRatio || '1:1', ['21:9', '9:21', '16:9', '9:16', '5:4', '4:5', '4:3', '3:4', '3:1', '1:3', '3:2', '2:3', '2:1', '1:2', '1:1'] as const, '1:1'),
                resolution: clampTo(image_resolution?.toLowerCase() || '1k', ['1k', '2k', '4k'] as const, '1k'),
                // 可选参数
                quality: clampTo(
                    providerOptions?.quality || 'medium',
                    ['low', 'medium', 'high', 'xhigh', 'max'] as const,
                    'medium',
                ),
                output_format: clampTo(
                    providerOptions?.output_format || 'png',
                    ['jpeg', 'png', 'webp'] as const,
                    'png',
                ),
            };
        },
    },
    'gpt-image-2.5-flare-edit': {
        // https://wavespeed.ai/zh-CN/models/openai/gpt-image-2.5-flare/edit
        apiPath: '/openai/gpt-image-2.5-flare/edit',
        pollApiPath: '/predictions/${requestId}/result',
        enableTranslation: false,
        buildInput: (params) => {
            const { prompt, aspectRatio, image_urls, image_resolution, providerOptions } = params;
            return {
                prompt: prompt,
                aspect_ratio: clampTo(aspectRatio || '1:1', ['21:9', '9:21', '16:9', '9:16', '5:4', '4:5', '4:3', '3:4', '3:1', '1:3', '3:2', '2:3', '2:1', '1:2', '1:1'] as const, '1:1'),
                resolution: clampTo(image_resolution?.toLowerCase() || '1k', ['1k', '2k', '4k'] as const, '1k'),
                // 图片 URL
                images: image_urls.slice(0, 16),
                // 可选参数
                quality: clampTo(
                    providerOptions?.quality || 'medium',
                    ['low', 'medium', 'high', 'xhigh', 'max'] as const,
                    'medium',
                ),
                output_format: clampTo(
                    providerOptions?.output_format || 'png',
                    ['jpeg', 'png', 'webp'] as const,
                    'png',
                ),
            };
        },
    },
    'gpt-image-2.5-sunburst-text-to-image': {
        // https://wavespeed.ai/zh-CN/models/openai/gpt-image-2.5-sunburst/text-to-image
        apiPath: '/openai/gpt-image-2.5-sunburst/text-to-image',
        pollApiPath: '/predictions/${requestId}/result',
        enableTranslation: false,
        buildInput: (params) => {
            const { prompt, aspectRatio, image_resolution, providerOptions } = params;
            return {
                prompt: prompt,
                aspect_ratio: clampTo(aspectRatio || '1:1', ['21:9', '9:21', '16:9', '9:16', '5:4', '4:5', '4:3', '3:4', '3:1', '1:3', '3:2', '2:3', '2:1', '1:2', '1:1'] as const, '1:1'),
                resolution: clampTo(image_resolution?.toLowerCase() || '1k', ['1k', '2k', '4k'] as const, '1k'),
                // 可选参数
                quality: clampTo(
                    providerOptions?.quality || 'medium',
                    ['low', 'medium', 'high', 'xhigh', 'max'] as const,
                    'medium',
                ),
                output_format: clampTo(
                    providerOptions?.output_format || 'png',
                    ['jpeg', 'png', 'webp'] as const,
                    'png',
                ),
            };
        },
    },
    'gpt-image-2.5-sunburst-edit': {
        // https://wavespeed.ai/zh-CN/models/openai/gpt-image-2.5-sunburst/edit
        apiPath: '/openai/gpt-image-2.5-sunburst/edit',
        pollApiPath: '/predictions/${requestId}/result',
        enableTranslation: false,
        buildInput: (params) => {
            const { prompt, aspectRatio, image_urls, image_resolution, providerOptions } = params;
            return {
                prompt: prompt,
                aspect_ratio: clampTo(aspectRatio || '1:1', ['21:9', '9:21', '16:9', '9:16', '5:4', '4:5', '4:3', '3:4', '3:1', '1:3', '3:2', '2:3', '2:1', '1:2', '1:1'] as const, '1:1'),
                resolution: clampTo(image_resolution?.toLowerCase() || '1k', ['1k', '2k', '4k'] as const, '1k'),
                // 图片 URL
                images: image_urls.slice(0, 16),
                // 可选参数
                quality: clampTo(
                    providerOptions?.quality || 'medium',
                    ['low', 'medium', 'high', 'xhigh', 'max'] as const,
                    'medium',
                ),
                output_format: clampTo(
                    providerOptions?.output_format || 'png',
                    ['jpeg', 'png', 'webp'] as const,
                    'png',
                ),
            };
        },
    },
    'gpt-image-2-text-to-image': {
        // https://wavespeed.ai/zh-CN/models/openai/gpt-image-2/text-to-image
        apiPath: '/openai/gpt-image-2/text-to-image',
        pollApiPath: '/predictions/${requestId}/result',
        enableTranslation: false,
        buildInput: (params) => {
            const { prompt, aspectRatio, image_resolution, providerOptions } = params;
            return {
                prompt: prompt,
                aspect_ratio: clampTo(aspectRatio || '16:9', ['21:9', '9:21', '16:9', '9:16', '5:4', '4:5', '4:3', '3:4', '3:1', '1:3', '3:2', '2:3', '2:1', '1:2', '1:1'] as const, '16:9'),
                resolution: clampTo(image_resolution?.toLowerCase() || '1k', ['1k', '2k', '4k'] as const, '1k'),
                // 可选参数
                quality: clampTo(
                    providerOptions?.quality || 'medium',
                    ['low', 'medium', 'high'] as const,
                    'medium',
                ),
                output_format: clampTo(
                    providerOptions?.output_format || 'png',
                    ['jpeg', 'png', 'webp'] as const,
                    'png',
                ),
            };
        },
    },
    'gpt-image-2-edit': {
        // https://wavespeed.ai/zh-CN/models/openai/gpt-image-2/edit
        apiPath: '/openai/gpt-image-2/edit',
        pollApiPath: '/predictions/${requestId}/result',
        enableTranslation: false,
        buildInput: (params) => {
            const { prompt, aspectRatio, image_urls, image_resolution, providerOptions } = params;
            return {
                prompt: prompt,
                aspect_ratio: aspectRatio === 'match' ? undefined : clampTo(aspectRatio || '1:1', ['21:9', '9:21', '16:9', '9:16', '5:4', '4:5', '4:3', '3:4', '3:1', '1:3', '3:2', '2:3', '2:1', '1:2', '1:1'] as const, '1:1'),
                resolution: clampTo(image_resolution?.toLowerCase() || '1k', ['1k', '2k', '4k'] as const, '1k'),
                // 图片 URL
                images: image_urls.slice(0, 16),
                // 可选参数
                quality: clampTo(
                    providerOptions?.quality || 'medium',
                    ['low', 'medium', 'high'] as const,
                    'medium',
                ),
                output_format: clampTo(
                    providerOptions?.output_format || 'png',
                    ['jpeg', 'png', 'webp'] as const,
                    'png',
                ),
            };
        },
    },
    'gpt-image-1.5-text-to-image': {
        // https://wavespeed.ai/zh-CN/models/openai/gpt-image-1.5/text-to-image
        apiPath: '/openai/gpt-image-1.5/text-to-image',
        pollApiPath: '/predictions/${requestId}/result',
        enableTranslation: false,
        buildInput: (params) => {
            const { prompt, aspectRatio, image_resolution, providerOptions } = params;
            return {
                prompt: prompt,
                size: ({ '1:1': '1024*1024', '2:3': '1024*1536', '3:2': '1536*1024' } as const)[
                    clampTo(aspectRatio || '1:1', ['3:2', '2:3', '1:1'] as const, '1:1')
                ],
                // 可选参数
                quality: clampTo(
                    providerOptions?.quality || 'medium',
                    ['low', 'medium', 'high'] as const,
                    'medium',
                ),
                output_format: clampTo(
                    providerOptions?.output_format || 'jpeg',
                    ['jpeg', 'png'] as const,
                    'jpeg',
                ),
                // system
                background: 'auto',
            };
        },
    },
    'gpt-image-1.5-edit': {
        // https://wavespeed.ai/zh-CN/models/openai/gpt-image-1.5/edit
        apiPath: '/openai/gpt-image-1.5/edit',
        pollApiPath: '/predictions/${requestId}/result',
        enableTranslation: false,
        buildInput: (params) => {
            const { prompt, aspectRatio, image_urls, providerOptions } = params;
            return {
                prompt: prompt,
                size: aspectRatio === 'match' ? 'auto' : ({ '1:1': '1024*1024', '2:3': '1024*1536', '3:2': '1536*1024' } as const)[
                    clampTo(aspectRatio || '1:1', ['3:2', '2:3', '1:1'] as const, '1:1')
                ],
                // 图片 URL
                images: image_urls.slice(0, 10),
                // 可选参数
                quality: clampTo(
                    providerOptions?.quality || 'medium',
                    ['low', 'medium', 'high'] as const,
                    'medium',
                ),
                input_fidelity: typeof providerOptions?.input_fidelity === 'string' ? providerOptions.input_fidelity : 'high',
                output_format: clampTo(
                    providerOptions?.output_format || 'jpeg',
                    ['jpeg', 'png'] as const,
                    'jpeg',
                ),
                // system
                background: 'auto',
            };
        },
    },
    'gpt-image-1-text-to-image': {
        // https://wavespeed.ai/zh-CN/models/openai/gpt-image-1/text-to-image
        apiPath: '/openai/gpt-image-1/text-to-image',
        pollApiPath: '/predictions/${requestId}/result',
        enableTranslation: false,
        buildInput: (params) => {
            const { prompt, aspectRatio, providerOptions } = params;
            return {
                prompt: prompt,
                size: ({ '1:1': '1024*1024', '2:3': '1024*1536', '3:2': '1536*1024' } as const)[
                    clampTo(aspectRatio || '1:1', ['3:2', '2:3', '1:1'] as const, '1:1')
                ],
                // 可选参数
                quality: clampTo(
                    providerOptions?.quality || 'medium',
                    ['low', 'medium', 'high'] as const,
                    'medium',
                ),
            };
        },
    },
    'gpt-image-1-edit': {
        // https://wavespeed.ai/zh-CN/models/openai/gpt-image-1
        apiPath: '/openai/gpt-image-1',
        pollApiPath: '/predictions/${requestId}/result',
        enableTranslation: false,
        buildInput: (params) => {
            const { prompt, aspectRatio, image_urls, image_resolution, providerOptions } = params;
            return {
                prompt: prompt,
                size: aspectRatio === 'match' ? 'auto' : ({ '1:1': '1024*1024', '2:3': '1024*1536', '3:2': '1536*1024' } as const)[
                    clampTo(aspectRatio || '1:1', ['3:2', '2:3', '1:1'] as const, '1:1')
                ],
                // 图片 URL
                image: image_urls[0],
                // 可选参数
                quality: clampTo(
                    providerOptions?.quality || 'medium',
                    ['low', 'medium', 'high'] as const,
                    'medium',
                ),
                // system
                mask_image: undefined,
            };
        },
    },
    'gpt-image-1-mini-text-to-image': {
        // https://wavespeed.ai/zh-CN/models/openai/gpt-image-1-mini/text-to-image
        apiPath: '/openai/gpt-image-1-mini/text-to-image',
        pollApiPath: '/predictions/${requestId}/result',
        enableTranslation: false,
        buildInput: (params) => {
            const { prompt } = params;
            return {
                prompt: prompt,
            };
        },
    },
    'gpt-image-1-mini-edit': {
        // https://wavespeed.ai/zh-CN/models/openai/gpt-image-1-mini/edit
        apiPath: '/openai/gpt-image-1-mini/edit',
        pollApiPath: '/predictions/${requestId}/result',
        enableTranslation: false,
        buildInput: (params) => {
            const { prompt, image_urls } = params;
            return {
                prompt: prompt,
                images: image_urls.slice(0, 4),
            };
        },
    },
};